'use client';

import React, { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/_hooks/UseIsMobile';
import { API_URL } from '@/_utils/Constants';
import { removeAllItems } from '@/_utils/StorageHelper';
import ErrorBox, { ErrorMessage } from '@/_components/ErrorBox';
import ImageAdaptive from '@/_components/ImageAdaptive';

function LoginContent() {
    const isMobile = useIsMobile();
    const searchParams = useSearchParams();
    const [loginErrorMessage, setLoginErrorMessage] = useState<ErrorMessage | null>(null);
    const [socialSignupErrorMessage, setSocialSignupErrorMessage] = useState<string | null>(null);
    const [socialSigninErrorMessage, setSocialSigninErrorMessage] = useState<string | null>(null);

    const handleSocialSignupUsingGoogle = () => {
        setLoginErrorMessage(null);
        setSocialSignupErrorMessage(null);
        setSocialSigninErrorMessage(null);

        window.location.href = `${API_URL}/ops/auth/signup/google`;
    };

    const handleSocialSigninUsingGoogle = () => {
        setLoginErrorMessage(null);
        setSocialSignupErrorMessage(null);
        setSocialSigninErrorMessage(null);
        window.location.href = `${API_URL}/ops/auth/signin/google`;
    };

    useEffect(() => {
        removeAllItems();
    }, []);

    useEffect(() => {
        const signupError = searchParams.get('signup_error');
        const loginError = searchParams.get('signin_error');
        if (signupError) {
            setSocialSignupErrorMessage(signupError);
        }
        if (loginError) {
            setSocialSigninErrorMessage(loginError);
        }
    }, [searchParams]);

    return (
        <>
            <main className="flex justify-center items-start lg:items-center w-full min-h-screen bg-color-bg overflow-y-auto overflow-x-hidden z-0 p-4">
                <motion.div
                    key="login"
                    initial={{ y: -100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 100, duration: 0.5 }}
                    className="flex flex-col justify-start items-center rounded-3xl bg-color-white-base shadow-right-bottom w-full lg:w-[1000px] h-auto min-h-auto lg:h-[680px] lg:min-h-[680px] bg-opacity-70"
                >
                    <div className="flex w-full pb-8">
                        <div className="flex w-[200px]"></div>
                        <div className="flex flex-grow justify-center items-end w-full px-2 pt-4 pb-4">
                            <div className="flex items-center font-nova font-bold text-color-base text-3xl lg:text-[45px] h-full">
                                ops.
                            </div>
                        </div>
                        <div className="flex w-[200px] z-100"></div>
                    </div>
                    <div className="flex h-[10px] min-h-[10px]"></div>
                    <div className="flex flex-col lg:flex-row w-full lg:w-[900px] lg:h-[480px] lg:min-h-[480px] p-1">
                        <div className="flex flex-col justify-start items-center w-full lg:w-[50%] h-[100%] px-2">
                            <div className="flex w-full h-full p-1 lg:p-4">
                                <div className="flex flex-col items-center rounded-3xl bg-color-white-base shadow-right-bottom border border-color-base w-full h-full">
                                    <div className="flex lg:flex-row w-[95%] font-montserrat font-black text-sm text-color-c-orange tracking-widest border-b border-color-c-orange px-2 pt-2">
                                        <div className="flex justify-start items-end">CREATE.</div>
                                        <div className="flex justify-center items-center w-full font-corp font-bold text-base text-color-c-orange tracking-widest">
                                            <ImageAdaptive
                                                src="/images/login/create-mark.svg"
                                                pcWidth={30}
                                                mbWidth={30}
                                            />
                                            クルーコードをもっていない
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-center items-center w-full h-full py-20 px-8">
                                        {socialSignupErrorMessage && <ErrorBox message={socialSignupErrorMessage} />}
                                        <div className="flex justify-center w-full">
                                            <motion.div
                                                whileHover={{ y: 2 }}
                                                className="flex items-center justify-center cursor-pointer px-4"
                                                onClick={handleSocialSignupUsingGoogle}
                                            >
                                                <ImageAdaptive
                                                    src="/images/login/btn-google.png"
                                                    pcWidth={55}
                                                    mbWidth={35}
                                                />
                                            </motion.div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col justify-start items-center w-full lg:w-[50%] h-[100%] px-2 pt-12 lg:pt-0">
                            <div className="flex w-full h-full p-1 lg:p-4">
                                <div className="flex flex-col items-center rounded-3xl bg-color-white-base shadow-right-bottom border border-color-base w-full h-full">
                                    <div className="flex lg:flex-row w-[95%] font-montserrat font-black text-sm text-color-base tracking-widest border-b border-color-base px-2 pt-2">
                                        <div className="flex justify-start items-end">LOGIN.</div>
                                        <div className="flex justify-center items-center w-full font-corp font-bold text-base text-color-base tracking-widest">
                                            <ImageAdaptive
                                                src="/images/login/login-mark.svg"
                                                pcWidth={30}
                                                mbWidth={30}
                                            />
                                            クルーコードをもっている
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-center items-center w-full h-full py-20 px-8">
                                        {socialSigninErrorMessage && <ErrorBox message={socialSigninErrorMessage} />}
                                        {loginErrorMessage && <ErrorBox message={loginErrorMessage.message} />}
                                        <div className="flex justify-center w-full">
                                            <motion.div
                                                whileHover={{ y: 2 }}
                                                className="flex items-center justify-center cursor-pointer px-4"
                                                onClick={handleSocialSigninUsingGoogle}
                                            >
                                                <ImageAdaptive
                                                    src="/images/login/btn-google.png"
                                                    pcWidth={55}
                                                    mbWidth={35}
                                                />
                                            </motion.div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </main>
        </>
    );
}

export default function Login() {
    return (
        <Suspense fallback={null}>
            <LoginContent />
        </Suspense>
    );
}
